'use client';

// Pending-documents WhatsApp reminder log for one claim: the actual message text sent,
// to which number, and when. Staff only; backed by GET .../reminder-logs.

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import type { ReminderLogEntry } from '@/lib/vehicle-claims/api';

const STAGE_LABELS: Record<number, string> = {
  1: 'Initial reminder (day 3)',
  2: 'Reminder 1 (day 10)',
  3: 'Reminder 2 (day 17)',
  4: 'Final reminder (day 24)',
};

const STATUS_BADGE_VARIANT: Record<string, 'success' | 'error' | 'default'> = {
  sent: 'success',
  failed: 'error',
  skipped: 'default',
};

const STATUS_LABELS: Record<string, string> = {
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
};

// PDF-only: drop the "(day N)" suffix from a stage label -- the on-screen
// dialog keeps it, only the downloaded PDF omits it per request.
function stripDaySuffix(label: string): string {
  return label.replace(/\s*\(day \d+\)\s*$/, '');
}

// Same company identity + address shown in the running header of the
// weasyprint-rendered reports (claim_survey_report.py) -- kept as plain
// text here too since this PDF is built client-side with jsPDF, not HTML/CSS.
const COMPANY_NAME = 'M/s. AXION TECHNICAL SERVICES PVT. LTD.';
const COMPANY_ADDRESS = 'L2/806, 48th Cross St, Thiruvanmiyur, Chennai 600041';

function formatTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  logs: ReminderLogEntry[];
  loading: boolean;
  error: string | null;
  claimNumber?: string | null;
}

export function ReminderLogDialog({ open, onOpenChange, logs, loading, error, claimNumber }: Props) {
  const [downloading, setDownloading] = useState(false);

  async function downloadPdf() {
    if (downloading || logs.length === 0) return;
    setDownloading(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const maxWidth = pageWidth - margin * 2;

      // Letterhead -- redrawn on every page (incl. after addPage()), same as
      // the running header on the weasyprint reports (navy #1e3a5f brand
      // color for the company name, centered).
      const centerX = pageWidth / 2;
      function drawLetterhead(): number {
        let hy = margin;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(30, 58, 95);
        doc.text(COMPANY_NAME, centerX, hy, { align: 'center' });
        hy += 13;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(100, 100, 100);
        doc.text(COMPANY_ADDRESS, centerX, hy, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        hy += 9;
        doc.setDrawColor(180, 180, 180);
        doc.line(margin, hy, pageWidth - margin, hy);
        return hy + 20;
      }

      let y = drawLetterhead();

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('Pending Documents Reminder Log', margin, y);
      y += 18;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Claim ${claimNumber ?? ''}`, margin, y);
      y += 24;

      function ensureSpace(lines: number, lineHeight = 13) {
        if (y + lines * lineHeight > pageHeight - margin) {
          doc.addPage();
          y = drawLetterhead();
        }
      }

      logs.forEach((log, i) => {
        ensureSpace(4);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        const stageLabel = stripDaySuffix(STAGE_LABELS[log.stage] ?? `Stage ${log.stage}`);
        const statusLabel = STATUS_LABELS[log.status] ?? log.status;
        doc.text(`${i + 1}. ${stageLabel} - ${statusLabel}`, margin, y);
        y += 14;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const meta = `${formatTime(log.created_at)}${log.whatsapp_number ? `  |  Sent to ${log.whatsapp_number}` : ''}`;
        doc.text(meta, margin, y);
        y += 14;

        if (log.message) {
          const lines: string[] = doc.splitTextToSize(log.message, maxWidth);
          ensureSpace(lines.length);
          doc.text(lines, margin, y);
          y += lines.length * 12 + 6;
        } else {
          doc.setFont('helvetica', 'italic');
          doc.text('No message was sent for this stage.', margin, y);
          doc.setFont('helvetica', 'normal');
          y += 16;
        }

        if (log.error_message) {
          const errLines: string[] = doc.splitTextToSize(log.error_message, maxWidth);
          ensureSpace(errLines.length);
          doc.setTextColor(180, 30, 30);
          doc.text(errLines, margin, y);
          doc.setTextColor(0, 0, 0);
          y += errLines.length * 12 + 6;
        }

        y += 10;
        if (i < logs.length - 1) {
          ensureSpace(1);
          doc.setDrawColor(220, 220, 220);
          doc.line(margin, y, pageWidth - margin, y);
          y += 14;
        }
      });

      const fileName = `reminder-log-${claimNumber ?? 'claim'}.pdf`;
      doc.save(fileName);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="full" className="max-w-5xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4 pr-8">
            <div>
              <DialogTitle>Pending Documents Reminder Log</DialogTitle>
              <DialogDescription>WhatsApp reminders sent to the customer for this claim.</DialogDescription>
            </div>
            <button
              onClick={downloadPdf}
              disabled={downloading || loading || logs.length === 0}
              className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {downloading ? 'Preparing...' : 'Download PDF'}
            </button>
          </div>
        </DialogHeader>

        <div className="max-h-[75vh] overflow-y-auto px-6 py-4">
          {loading && <p className="text-sm text-gray-500">Loading...</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!loading && !error && logs.length === 0 && (
            <p className="text-sm text-gray-500">No reminders have been sent for this claim.</p>
          )}
          {!loading && !error && logs.length > 0 && (
            <ul className="space-y-3">
              {logs.map((log, i) => (
                <li key={i} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {STAGE_LABELS[log.stage] ?? `Stage ${log.stage}`}
                      </span>
                      <Badge variant={STATUS_BADGE_VARIANT[log.status] ?? 'default'}>
                        {STATUS_LABELS[log.status] ?? log.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatTime(log.created_at)}
                      {log.whatsapp_number && <> &middot; sent to <span className="font-medium text-gray-700">{log.whatsapp_number}</span></>}
                    </div>
                  </div>

                  {log.message ? (
                    <pre className="mt-3 whitespace-pre-wrap rounded-md bg-gray-50 p-3 font-sans text-xs text-gray-700">
                      {log.message}
                    </pre>
                  ) : (
                    <p className="mt-3 text-xs italic text-gray-400">No message was sent for this stage.</p>
                  )}

                  {log.error_message && (
                    <p className="mt-2 text-xs text-red-600">{log.error_message}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
